import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Summarize as SummarizeIcon,
  Info as InfoIcon,
  Bolt as BoltIcon,
  BoltOutlined as BoltOutlinedIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { Lightning } from '@phosphor-icons/react';
import { GeneratedSummary } from '@/types/templateSummaryTypes';

interface SummariesTabProps {
  sessionId?: string;
  summaries: GeneratedSummary[];
  isLoading?: boolean;
}

export const SummariesTab: React.FC<SummariesTabProps> = ({
  sessionId,
  summaries,
  isLoading = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(
    new Set()
  );
  const [copiedSummary, setCopiedSummary] = useState<string | null>(null);

  // Auto-expand the first summary
  useEffect(() => {
    if (summaries.length > 0 && !isLoading) {
      setExpandedSummaries(new Set([summaries[0].id]));
    }
  }, [summaries, isLoading]);

  const toggleSummaryExpansion = (summaryId: string) => {
    setExpandedSummaries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(summaryId)) {
        newSet.delete(summaryId);
      } else {
        newSet.add(summaryId);
      }
      return newSet;
    });
  };

  const handleCopySummary = async (summary: GeneratedSummary) => {
    try {
      await navigator.clipboard.writeText(summary.content);
      setCopiedSummary(summary.id);
      setTimeout(() => setCopiedSummary(null), 2000);
    } catch (error) {
      console.error('Failed to copy summary:', error);
    }
  };

  const handleDownloadSummary = (summary: GeneratedSummary) => {
    const blob = new Blob([summary.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${summary.name
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()}_summary.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getAudienceColor = (audience: string) => {
    const colors = {
      'Internal Team': 'primary',
      'Legal Department': 'secondary',
      Management: 'success',
      'Public Information': 'info',
      'Law Enforcement': 'warning',
      'Medical Team': 'error'
    };
    return colors[audience as keyof typeof colors] || 'default';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: isMobile ? 1.5 : 3, py: isMobile ? 1.5 : 2 }}>
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              minHeight: summaries.length === 0 ? '300px' : 'auto',
              mb: summaries.length > 0 ? 3 : 0
            }}
          >
            <CircularProgress size={26} />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{
                fontSize: '13px',
                fontWeight: 400,
                letterSpacing: '-0.005em'
              }}
            >
              {summaries.length === 0
                ? 'Generating summaries...'
                : 'Updating summaries...'}
            </Typography>
          </Box>
        )}
        {summaries.length === 0 && !isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px'
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: 'center',
                fontSize: '13px',
                fontWeight: 400,
                letterSpacing: '-0.005em',
                lineHeight: 1.5,
                maxWidth: '300px'
              }}
            >
              No summaries generated yet
            </Typography>
          </Box>
        ) : summaries.length > 0 ? (
          <Stack spacing={isMobile ? 2.5 : 2}>
            {summaries.map((summary, summaryIndex) => (
              <Accordion
                key={`${summary.id}-${summaryIndex}`}
                expanded={expandedSummaries.has(summary.id)}
                onChange={() => toggleSummaryExpansion(summary.id)}
                sx={{
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: '0 !important'
                  },
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                  },
                  overflow: 'hidden',
                  marginBottom: summaryIndex < summaries.length - 1 ? (isMobile ? 2.5 : 2) : 0,
                  '&:last-of-type': {
                    marginBottom: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />}
                  sx={{
                    minHeight: isMobile ? '72px' : '56px',
                    py: isMobile ? 1.5 : 1,
                    '& .MuiAccordionSummary-content': {
                      margin: isMobile ? '8px 0' : '12px 0',
                      overflow: 'hidden'
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.02)' 
                        : '#fafafa'
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: isMobile ? 1.5 : 2,
                      width: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Avatar Icon */}
                    <Box
                      sx={{
                        width: isMobile ? 32 : 36,
                        height: isMobile ? 32 : 36,
                        borderRadius: '6px',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : '#f3f4f6',
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.text.secondary,
                        flexShrink: 0
                      }}
                    >
                      <Lightning size={isMobile ? 16 : 18} weight="light" />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, mr: isMobile ? 0.5 : 0 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          lineHeight: 1.4,
                          letterSpacing: '-0.01em',
                          mb: 0.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {summary.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 400,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.5,
                          letterSpacing: '-0.005em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {summary.audience} • {new Date(summary.generatedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: isMobile ? 0.25 : 1,
                        alignItems: 'center',
                        flexShrink: 0,
                        ml: 'auto'
                      }}
                    >
                      <Tooltip title={copiedSummary === summary.id ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopySummary(summary);
                          }}
                          sx={{
                            color: copiedSummary === summary.id 
                              ? theme.palette.success.main 
                              : theme.palette.text.secondary,
                            width: isMobile ? 32 : 36,
                            height: isMobile ? 32 : 36,
                            padding: isMobile ? '6px' : '8px'
                          }}
                        >
                          <CopyIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download as text file">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadSummary(summary);
                          }}
                          sx={{
                            color: theme.palette.text.secondary,
                            width: isMobile ? 32 : 36,
                            height: isMobile ? 32 : 36,
                            padding: isMobile ? '6px' : '8px'
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                        </IconButton>
                      </Tooltip>
                      {!isMobile && (
                        <Tooltip title="Share summary">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Implement sharing functionality
                            }}
                            sx={{
                              color: theme.palette.text.secondary
                            }}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      textAlign: 'left',
                      '& h1': {
                        fontSize: '20px',
                        fontWeight: 600,
                        mt: isMobile ? 1.5 : 2,
                        mb: 1,
                        textAlign: 'left',
                        lineHeight: 1.4,
                        letterSpacing: '-0.025em',
                        color: theme.palette.text.primary
                      },
                      '& h2': {
                        fontSize: '16px',
                        fontWeight: 600,
                        mt: isMobile ? 1.5 : 2,
                        mb: 0.75,
                        textAlign: 'left',
                        lineHeight: 1.4,
                        letterSpacing: '-0.02em',
                        color: theme.palette.text.primary
                      },
                      '& h3': {
                        fontSize: '14px',
                        fontWeight: 600,
                        mt: isMobile ? 1.25 : 1.5,
                        mb: 0.75,
                        textAlign: 'left',
                        lineHeight: 1.4,
                        letterSpacing: '-0.01em',
                        color: theme.palette.text.primary
                      },
                      '& p': {
                        mb: 1.25,
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        letterSpacing: '-0.005em',
                        color: theme.palette.text.primary
                      },
                      '& ul, & ol': {
                        pl: isMobile ? 2.5 : 3,
                        mb: 1.25,
                        textAlign: 'left'
                      },
                      '& li': {
                        mb: 0.5,
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        letterSpacing: '-0.005em',
                        color: theme.palette.text.primary
                      },
                      '& code': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.1)' 
                          : 'grey.200',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontSize: '12px',
                        fontWeight: 400,
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace'
                      },
                      '& strong': {
                        fontWeight: 600,
                        color: theme.palette.text.primary
                      },
                      '& em': {
                        fontStyle: 'italic'
                      }
                    }}
                  >
                    <ReactMarkdown>{summary.content}</ReactMarkdown>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
};
